import {
  buildCategoryTree,
  type CatalogCategoryNodeInput,
  type CatalogCategoryTreeNode,
} from "./catalog-category-tree";

// What the target-cost surfaces say (#1216): the toast's copy over the spec's
// over-target count, and the Item Search chip's label and price.

/** The italic sentence after the count (`target-cost-notification.tsx`). */
export const OVER_TARGET_NOTE = "Target cost is only visible to Division and National Editors.";

/**
 * The over-target callout — `TargetCostNotification`'s content
 * (`target-cost-notification.tsx`), shared so "Where to save"'s dialog alert
 * (#1217) can say the same thing.
 *
 * A count above 0 is a warning: the bold count, a space, `sentence`, then a
 * space and the italic `note`. PB-T3 (#1165 Q16) gives
 * one selection `1 item exceeds target cost.` and puts the space before the
 * note, where the donor reads `1 items…` and runs the two sentences together.
 * 0 is the success line, and a null count reads 0, as the donor's `|| 0` does.
 */
export type OverTargetCallout =
  | { severity: "warning"; count: number; sentence: string; note: string }
  | { severity: "success"; message: string };

export function overTargetCallout(count: number | null): OverTargetCallout {
  const n = count ?? 0;
  if (n > 0) {
    return {
      severity: "warning",
      count: n,
      sentence: n === 1 ? "item exceeds target cost." : "items exceed target cost.",
      note: OVER_TARGET_NOTE,
    };
  }
  return {
    severity: "success",
    message: "Congratulations! You have no items that exceed target cost.",
  };
}

/**
 * The over-target reads' failure (`over-target-items.tsx`): the Spec list's
 * Over Target popover's, and "Where to save"'s dialog's (#1217, PB-T11), so the
 * two read the same.
 */
export const OVER_TARGET_ERROR_LINES = [
  "Failed to load items",
  "Please close popup and try again.",
] as const;

/** The one Appliances category the donor prices as a max price. */
const GARBAGE_DISPOSAL_SLUG = "garbage-disposal";

/**
 * The categories whose items read `Lennar Price:` — Falcon's
 * `appliancesCategories` (`get_category_and_children`, `item_search_island/
 * router.py:84-118`): the first node named exactly `Appliances` that a
 * breadth-first walk of the nested tree meets, and every category under it,
 * less any whose slug is `garbage-disposal` (`ItemCard.tsx:310-322`, the
 * card's own exclusion). Walked over `buildCategoryTree`'s nesting rather than
 * the read's `depth`, which that module does not trust.
 *
 * Empty until the tree has loaded, as the donor's empty `appliancesCategories`
 * is, so every card reads `Max Price:` until then.
 */
export function lennarPriceCategoryIds(
  nodes: readonly CatalogCategoryNodeInput[] | undefined,
): ReadonlySet<number> {
  const ids = new Set<number>();
  if (!nodes) return ids;
  const breadthFirst = (
    from: CatalogCategoryTreeNode[],
    visit: (node: CatalogCategoryTreeNode) => boolean,
  ) => {
    const queue = [...from];
    for (let node = queue.shift(); node; node = queue.shift()) {
      if (visit(node)) return node;
      queue.push(...node.children);
    }
    return undefined;
  };
  const root = breadthFirst(buildCategoryTree(nodes), (node) => node.name === "Appliances");
  if (!root) return ids;
  breadthFirst([root], (node) => {
    if (node.slug !== GARBAGE_DISPOSAL_SLUG) ids.add(node.id);
    return false;
  });
  return ids;
}

export type MaxPriceLabel = "Lennar Price:" | "Max Price:";

/** The chip's label for an item in this category. */
export function maxPriceLabel(
  categoryId: number | undefined,
  lennarPriceIds: ReadonlySet<number>,
): MaxPriceLabel {
  return categoryId !== undefined && lennarPriceIds.has(categoryId)
    ? "Lennar Price:"
    : "Max Price:";
}

/**
 * The chip's price — `$` and `Number(v).toFixed(2)` (`item-max-price.tsx`), no
 * thousands separator — or nothing, for no price or a zero one. The wire
 * carries `maxPrice` as a string, so a zero arrives as `"0.00"`, which the
 * donor's `!maxPrice` would print; #1165 Q20 hides null and 0, so it is hidden.
 */
export function maxPriceText(maxPrice: string | null | undefined): string | null {
  if (!maxPrice) return null;
  const value = Number(maxPrice);
  if (value === 0 || Number.isNaN(value)) return null;
  return `$${value.toFixed(2)}`;
}

/**
 * "Where to save"'s price (#1217) — the donor table's `formatPrice`
 * (`cost-saving-summary-table.tsx`): `$` and two decimals, no thousands
 * separator, over the wire's decimal string. A zero prints `$0.00`: the
 * dialog hides nothing, unlike `maxPriceText`.
 */
export function whereToSavePrice(value: string): string {
  const price = decimal(value);
  return price === null ? "" : `$${price.toFixed(2)}`;
}

/**
 * "Where to save"'s share over contract max — the donor's `formatPercentage`:
 * the stored fraction (#1211's `over_contract_percentage`) times 100, two
 * decimals and `%`.
 */
export function whereToSaveShare(value: string): string {
  const share = decimal(value);
  return share === null ? "" : `${(share * 100).toFixed(2)}%`;
}

/**
 * A wire decimal string as a number, or `null` for one that is not a finite
 * number — blank included, which `Number` reads as 0. The wire types the
 * prices as plain strings, so the dialog prints nothing for one that is not a
 * number rather than `$NaN` (FinalWalk, #1243).
 */
function decimal(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
